// What the professional's app says about forms: the chip, the Today line, the
// frequency choices, and the small rules behind Add to health flags. Plain
// functions, so the screens only lay them out.

import { ApiError } from '../api/client'
import { saveErrorMessage } from '../screens/settings/toast'
import { SHORT_MONTHS } from '../catalog/format'
import type { FormFrequency, HealthAnswer } from '../api/client'
import type { Preference, PreferenceInput } from '../types/app'

export type FormChipTone = 'warning' | 'positive' | 'info'

export interface FormChipView {
  label: string
  tone: FormChipTone
}

type ChipStatus = 'needed' | 'updated' | 'signed' | 'none' | null | undefined

// formChip is the chip on an appointment or a client's form; no form to show,
// or none asked for yet, is no chip.
export function formChip(status: ChipStatus): FormChipView | null {
  switch (status) {
    case 'needed':
      return { label: 'Form needed', tone: 'warning' }
    case 'signed':
      return { label: 'Form signed', tone: 'positive' }
    case 'updated':
      return { label: 'Updated', tone: 'info' }
    default:
      return null
  }
}

// FormRowView is the appointment sheet's form row: a coloured dot, what the
// form's state is in words, and the one thing to do about it.
export interface FormRowView {
  tone: 'warning' | 'positive' | 'info' | 'neutral'
  text: string
  action: 'send' | 'send-again' | 'view'
}

// formRow says where the client's form stands for an appointment; null when
// there is no form to show. signedAt dates a signed or updated form.
export function formRow(status: ChipStatus, signedAt: string | null | undefined, today: Date): FormRowView | null {
  const on = signedAt ? ` ${signedDay(signedAt, today)}` : ''
  switch (status) {
    case 'needed':
      return { tone: 'warning', text: 'Not filled in yet', action: 'send-again' }
    case 'none':
      return { tone: 'neutral', text: 'Not asked', action: 'send' }
    case 'signed':
      return { tone: 'positive', text: `Signed${on}`, action: 'view' }
    case 'updated':
      return { tone: 'info', text: `Updated${on}`, action: 'view' }
    default:
      return null
  }
}

// signedDay is "17 Oct", with the year when it isn't this year's.
function signedDay(iso: string, today: Date): string {
  const [y, m, d] = iso.slice(0, 10).split('-').map(Number)
  const day = `${d} ${SHORT_MONTHS[m - 1]}`
  return y === today.getFullYear() ? day : `${day} ${y}`
}

// formsNeededLine is Today's line for clients whose form is still to do.
export function formsNeededLine(n: number): string | null {
  if (n <= 0) return null
  return n === 1 ? "1 client today hasn’t filled in their form." : `${n} clients today haven’t filled in their form.`
}

export const FREQUENCY_CHOICES: { value: FormFrequency; label: string }[] = [
  { value: 'first_visit', label: 'First visit only' },
  { value: 'every_booking', label: 'Every booking' },
  { value: 'months', label: 'Every few months' },
]

export const DEFAULT_REFRESH_MONTHS = 12

// refreshMonthsFor is the number of months to save with a frequency: only
// "months" has one.
export function refreshMonthsFor(frequency: FormFrequency, current: number | null | undefined): number | null {
  return frequency === 'months' ? current ?? DEFAULT_REFRESH_MONTHS : null
}

// healthFlagInputs is the whole preferences list to PUT with one more health
// flag, or null when she already has that flag (nothing to add).
export function healthFlagInputs(existing: Preference[], answer: HealthAnswer): PreferenceInput[] | null {
  if (existing.some((p) => p.is_health_flag && p.label === answer.label && p.value === answer.value)) return null
  return [
    ...existing.map((p) => ({ label: p.label, value: p.value, is_health_flag: p.is_health_flag })),
    { label: answer.label, value: answer.value, is_health_flag: true },
  ]
}

// sendFormMessage explains a failed Send form. 429: one went out recently;
// 422: there is nowhere to send it.
export function sendFormMessage(error: unknown): string {
  if (error instanceof ApiError && error.status === 429) return 'A form was sent to them recently. Try again later.'
  if (error instanceof ApiError && error.status === 422) return 'They have no email address. Add one to their profile first.'
  return saveErrorMessage(error)
}
